import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Figure, Flow } from '../components/Diagram';

/** A capability in plain words, with the tool id the Assistant uses for it. */
type Item = [capability: string, id: string, detail: string];

interface Group {
  title: string;
  items: Item[];
}

const READS: Group[] = [
  {
    title: 'Clients and subscriptions',
    items: [
      [
        'Find clients',
        'query_clients',
        'Look up clients by name, description or contact, or list them.',
      ],
      [
        'List subscriptions',
        'query_subscriptions',
        'Subscriptions for a client or the whole workspace, with period, price, catalog pins and renewal dates.',
      ],
      [
        'Explain how a subscription bills',
        'get_billing_schedule',
        'Why a subscription bills when it does: a prepaid span, or a deal signed and waiting for go-live.',
      ],
      [
        'Look up discounts',
        'query_discounts',
        'The discount library, and for one subscription its components, stacking mode and applied discounts.',
      ],
      [
        'Read the lifecycle log',
        'query_lifecycle_events',
        'Created, renewed, expanded, contracted, churned and migrated events, with the MRR change of each.',
      ],
    ],
  },
  {
    title: 'Invoices and receivables',
    items: [
      [
        'List invoices',
        'query_invoices',
        'Invoices by client, subscription or payment status. Credit notes are flagged.',
      ],
      [
        'Check what is owed',
        'get_receivables',
        'Pending and overdue totals by currency, past-due accounts, invoices due this week, one client outstanding.',
      ],
      [
        'Check email engagement',
        'query_email_engagement',
        'Sent invoice and reminder emails with open counts and open rate.',
      ],
    ],
  },
  {
    title: 'Revenue analytics',
    items: [
      [
        'Report live revenue numbers',
        'get_analytics',
        'MRR movement, ARR, churn rate and NRR in your reporting currency.',
      ],
    ],
  },
  {
    title: 'Metered usage',
    items: [
      [
        'Read Meter usage',
        'query_usage',
        'Live counters, the usage audit log, or the projected usage charge of the next invoice.',
      ],
      [
        'Find missing usage quantities',
        'list_open_usage_readings',
        'Closed periods that need a quantity before their invoice can issue.',
      ],
    ],
  },
  {
    title: 'Catalog',
    items: [
      ['List regions and currencies', 'list_regions', 'The markets you sell in.'],
      [
        'List the catalog',
        'list_catalog',
        'Bundles, modules, usage components and Meters, with draft or published status.',
      ],
      [
        'Read base prices',
        'get_catalog_prices',
        'Base price of a bundle or module by region, currency and period.',
      ],
      [
        'Read usage prices',
        'get_component_prices',
        "Included units, tier or step boundaries and prices of a plan's usage components.",
      ],
    ],
  },
  {
    title: 'Guidance',
    items: [
      [
        'Search the documentation',
        'search_docs',
        'Answers how Revvly works from the knowledge base, with links.',
      ],
      [
        'Explain pricing models',
        'explain_pricing_models',
        'What each usage pricing model is, when to use it, and the fields it needs.',
      ],
      [
        'Offer a choice',
        'offer_choices',
        'Shows two to five options as a picker when your request is ambiguous.',
      ],
      [
        'Offer pricing models',
        'offer_pricing_models',
        'Shows pricing model options with a worked example each, so you pick one.',
      ],
    ],
  },
  {
    title: 'CRM',
    items: [
      ['Search the CRM', 'search_crm', 'Companies, contacts and deals by name, domain or email.'],
      [
        'Read a timeline',
        'get_crm_timeline',
        'Notes, documents, emails and tasks for a company, contact or deal.',
      ],
      ['List deals', 'list_leads', 'Deals filtered by pipeline, stage, status, company or owner.'],
      ['Read one deal', 'get_lead', 'One deal by id.'],
      [
        'Analyse lost deals',
        'search_lost_deals',
        'The reasons reps recorded when deals were lost. Only deals in pipelines you can see.',
      ],
      ['List pipelines', 'list_pipelines', 'Pipelines and their stages.'],
    ],
  },
  {
    title: 'Tasks',
    items: [
      ['List tasks', 'list_tasks', 'Tasks by status, assignee or related record.'],
      ['Read one task', 'get_task', 'One task by id.'],
    ],
  },
  {
    title: 'Forms',
    items: [
      ['List forms', 'list_forms', 'Forms with status, submission counts and links.'],
      ['Read a form setup', 'get_form', 'Questions, confirmation settings and follow-up steps.'],
      [
        'Read submissions',
        'query_form_submissions',
        'Submissions with every answer and the outcome of each follow-up step.',
      ],
      [
        'Summarise submissions',
        'summarize_form_submissions',
        'Counts per day and source, failures, and which option wins.',
      ],
      [
        'Export submissions',
        'export_form_submissions',
        'A CSV preview, with the dashboard page for the full file.',
      ],
    ],
  },
  {
    title: 'OKR hypotheses',
    items: [
      [
        'Find Key Results falling behind',
        'list_behind_key_results',
        'Key Results of the active cycle behind their pace or projection, with progress, owner and open hypotheses.',
      ],
      [
        'Read what was tried on a Key Result',
        'get_key_result_hypotheses',
        'Its numbers, open hypotheses, concluded ones with their learnings, and related learnings from the library.',
      ],
    ],
  },
  {
    title: 'Memory',
    items: [
      [
        'Show what it remembers',
        'list_memories',
        "The Business profile, the workspace's memories and your own, as cards. Other people's personal memories are never shown.",
      ],
    ],
  },
  {
    title: 'Work Inbox',
    items: [
      [
        'List your inbox',
        'list_inbox',
        'Threads in one tab, optionally one category, with sender, subject, a short snippet and the reason for the category. Never message bodies.',
      ],
      [
        'Say what needs your reply',
        'get_inbox_summary',
        'Counts per tab and category, and the threads waiting longest for your reply.',
      ],
      [
        'Explain why a thread is filed where it is',
        'explain_inbox_category',
        'What decided its category, the reason line and the evidence in plain words.',
      ],
    ],
  },
  {
    title: 'Daily Check-ins',
    items: [
      [
        'List check-ins',
        'list_checkins',
        'Name, status, who is asked and when, the report channel and the next run date.',
      ],
      [
        'Read one check-in',
        'get_checkin',
        'Its questions, schedule, report channel and who it asks, including anyone Slack cannot reach.',
      ],
      [
        'Read the setup options',
        'get_checkin_options',
        'Whether Slack is connected, the channels, teams and people to choose from, the starter questions and the limits.',
      ],
    ],
  },
  {
    title: 'The page behind the chat',
    items: [
      [
        'Open a dashboard page',
        'navigate_to',
        'Moves the page behind the chat to the one being talked about. It changes what you see, never what is stored.',
      ],
      [
        'Point at a record',
        'highlight_record',
        'Opens the page of a client, subscription, module or bundle if needed and marks it for a moment, typically one just created.',
      ],
    ],
  },
];

const LOW: Group[] = [
  {
    title: 'CRM',
    items: [
      ['Log a note', 'create_note', 'An internal note on a company, contact or deal.'],
      [
        'Log a past email',
        'log_email',
        'Records an email that already happened. It never sends anything.',
      ],
      ['Log a meeting', 'log_meeting', 'Records a meeting. It never books or invites anyone.'],
      [
        'Create a prospect',
        'create_prospect',
        'A prospect company. It never creates billing state.',
      ],
      [
        'Create a contact',
        'create_contact',
        'A contact on a company. Emails are unique across the workspace.',
      ],
      [
        'Create a deal',
        'create_lead',
        'A deal, on an existing company or a new one, in the default or a chosen pipeline.',
      ],
    ],
  },
  {
    title: 'Tasks',
    items: [
      ['Create a task', 'create_task', 'With due date, priority, assignees and a related record.'],
      [
        'Edit a task',
        'update_task',
        'Name, description, priority, due date, reminder or relation.',
      ],
      [
        'Mark a task done, reopen it or snooze it',
        'set_task_status',
        'The reversible statuses only.',
      ],
      ['Assign a task', 'assign_task', 'Replaces the assignees. An empty list clears them.'],
    ],
  },
];

const GUARDED: Group[] = [
  {
    title: 'Clients and subscriptions',
    items: [
      [
        'Create a client',
        'create_client',
        'Name, region, billing details, payment terms, trial window and contacts.',
      ],
      [
        'Edit a client',
        'update_client',
        'Only the fields you name change. Protected against stale edits.',
      ],
      [
        'Create a subscription',
        'create_subscription',
        'For a client, on a published bundle, modules or usage components. Can pick a module tier, set the starting quantity of usage components, and queue discounts, all shown on the approval card.',
      ],
      [
        'Apply a discount',
        'apply_discount',
        'A library or one-off discount on the base price, one usage component or the whole invoice: percentage or fixed amount, duration, fixed mode, unit limit, end date and adjustment invoices. Applies from the next invoice.',
      ],
    ],
  },
  {
    title: 'Catalog',
    items: [
      [
        'Build a complete plan',
        'create_plan',
        'Meter, usage component, module or bundle, base prices, attachment and publish, in one approval. It stops at the first failure and reports each step.',
      ],
      ['Create a bundle', 'create_bundle', 'As a draft.'],
      ['Create a module', 'create_module', 'As a draft, optionally with tiers.'],
      ['Create a Meter', 'create_metric', 'The unit billing is measured against.'],
      [
        'Create a usage component',
        'create_variable_component',
        'A pricing model around a Meter, as a draft.',
      ],
      [
        'Set base prices',
        'set_catalog_prices',
        'Per region, currency and period. Works on published plans too.',
      ],
      [
        'Set billing periods',
        'set_available_periods',
        'Monthly, quarterly, yearly. Replaces the set on a draft.',
      ],
      [
        'Attach a usage component',
        'attach_variable_component',
        'A published component onto a draft module or bundle.',
      ],
      [
        'Publish a catalog item',
        'publish_catalog_entity',
        'Draft to published. Publishing makes it immutable.',
      ],
      [
        'Branch a new draft version',
        'create_catalog_version',
        'An editable draft copy of a published bundle, module or component.',
      ],
    ],
  },
  {
    title: 'Metered usage',
    items: [
      [
        'Enter a missing usage quantity',
        'enter_usage_reading',
        'Seals a closed period so its invoice can issue.',
      ],
      [
        'Waive a usage period',
        'waive_usage_reading',
        'The period bills nothing. A reason is recorded.',
      ],
    ],
  },
  {
    title: 'CRM',
    items: [
      [
        'Move a deal to another stage',
        'move_lead',
        'Moving to a won or lost stage marks the deal won or lost, and a lost reason can be recorded.',
      ],
      [
        'Change a lifecycle stage',
        'update_lifecycle_stage',
        'Prospect, lead or disqualified. Client is not settable here.',
      ],
      [
        'Change an account owner',
        'update_owner',
        'Hand a company or contact to another person, or clear the owner.',
      ],
    ],
  },
  {
    title: 'Tasks',
    items: [['Cancel a task', 'cancel_task', 'Cancelling is a final state, so it asks first.']],
  },
  {
    title: 'Forms',
    items: [
      ['Create a form', 'create_form', 'As an unpublished draft.'],
      [
        'Edit a form draft',
        'update_form',
        'Questions, settings and steps. A list you send replaces the whole list. Protected against stale edits.',
      ],
      [
        'Publish a form',
        'publish_form',
        'Makes the draft live. The dashboard publish checks apply.',
      ],
      ['Take a form offline', 'unpublish_form', 'Submissions are kept.'],
      ['Archive a form', 'archive_form', 'Nothing is deleted.'],
      ['Retry a failed submission', 'retry_form_submission', 'Re-runs only the steps that failed.'],
    ],
  },
  {
    title: 'OKR hypotheses',
    items: [
      [
        'Propose hypotheses for a Key Result',
        'propose_hypotheses',
        'Up to three testable bets, each in Backlog, as if you had proposed them. Nothing is advanced or assigned.',
      ],
    ],
  },
  {
    title: 'Memory',
    items: [
      [
        'Remember something',
        'remember',
        'Keeps a preference or a rule of the business for later conversations, for you or, if you are an admin, for everyone.',
      ],
      [
        'Reword a memory',
        'update_memory',
        'Shows the old and new wording. If the memory was changed elsewhere after the card appeared, nothing is written.',
      ],
      [
        'Forget a memory',
        'forget_memory',
        'Shows the memory before it is removed. Workspace memories need an admin.',
      ],
    ],
  },
  {
    title: 'Work Inbox',
    items: [
      [
        'Move an inbox thread',
        'move_inbox_item',
        'Files one of your threads under another category. Your choice is kept, and it teaches the inbox like a move in the dashboard.',
      ],
    ],
  },
  {
    title: 'Daily Check-ins',
    items: [
      [
        'Create a check-in',
        'create_checkin',
        'Who is asked, when, the questions and the report channel. It starts active, and saving posts a line in the channel to prove the bot can post there.',
      ],
      [
        'Edit a check-in',
        'update_checkin',
        'Only what you name changes, from the next run. A questions list replaces the whole list. Protected against stale edits.',
      ],
      [
        'Pause, resume or archive a check-in',
        'set_checkin_status',
        'Archiving is permanent, though its history is kept. Protected against stale edits.',
      ],
      [
        'Send a test',
        'send_checkin_test',
        'The questions go to you as a Slack direct message and a test line is posted in the report channel. Nothing is recorded.',
      ],
    ],
  },
];

const count = (groups: Group[]) => groups.reduce((n, g) => n + g.items.length, 0);

function Groups({ groups }: { groups: Group[] }): ReactNode {
  return (
    <>
      {groups.map((group) => (
        <div key={group.title}>
          <h3 className="api-sub-heading">{group.title}</h3>
          <div className="dp-fields-table api-field-table-mb">
            {group.items.map(([capability, id, detail]) => (
              <div className="dp-field-row" key={id}>
                <div className="dp-field-k">
                  {capability}
                  <br />
                  <span className="api-inline">{id}</span>
                </div>
                <div className="dp-field-v">{detail}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

export function AssistantCapabilities() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'What the Revvly Assistant can do — Revvly Docs';
  }, []);

  const reads = count(READS);
  const lows = count(LOW);
  const guarded = count(GUARDED);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/reference">Reference</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">What the Revvly Assistant can do</span>
      </nav>
      <header className="dp-header">
        <span className="rv-eyebrow-mini">Reference</span>
        <h1>
          What the Revvly <em>Assistant</em> can do.
        </h1>
        <p className="dp-oneliner">
          {reads + lows + guarded} tools in three kinds: {reads} that only read, {lows} low-risk
          writes that run straight away, and {guarded} guarded writes that wait for your approval.
          How to use it day to day is in <Link to="/docs/assistant">the Assistant guide</Link>.
        </p>
      </header>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">01</span>
          The three kinds of tool
        </h2>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Read ({reads})</div>
            <div className="dp-field-v">
              Run immediately on your live data, limited to your workspace. They change nothing.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Low-risk write ({lows})</div>
            <div className="dp-field-v">
              Reversible, small changes: a note, a task, a logged email or meeting, a new prospect,
              contact or deal. They run immediately and the Assistant tells you what it did. If you
              later decline an approval card in the same turn, these are not undone.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Guarded write ({guarded})</div>
            <div className="dp-field-v">
              Changes to clients, subscriptions, the catalog, usage readings, deal stages and
              owners, forms, new OKR hypotheses, inbox moves, check-ins, and what the Assistant
              remembers. Each one becomes an approval card and nothing is written until you approve
              it.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Deletes</div>
            <div className="dp-field-v">
              The Assistant cannot delete your records. The one exception is forgetting a memory it
              was asked to keep, which is a guarded write like any other.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">02</span>
          Approval, expiry and stale edits
        </h2>
        <Figure
          title="Life of an approval card"
          caption="A guarded change is written only on a timely approval of a record nobody else has changed."
        >
          <Flow
            steps={[
              {
                title: 'You ask',
                text: 'For a change to a client, subscription, deal stage or form.',
              },
              {
                title: 'Approval card',
                text: 'A summary with names instead of ids. Nothing is written yet.',
              },
              {
                fork: [
                  {
                    title: 'You approve',
                    text: 'Runs exactly what was proposed, once. The Assistant reports back.',
                    tone: 'accent',
                  },
                  { title: 'You decline', text: 'Nothing runs.', tone: 'muted' },
                  {
                    title: '15 minutes pass',
                    text: 'It can no longer be approved. The Assistant can propose again.',
                    tone: 'warn',
                  },
                  {
                    title: 'The record changed',
                    text: 'Nothing is written. The Assistant reads again and proposes fresh.',
                    tone: 'warn',
                  },
                ],
              },
            ]}
          />
        </Figure>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Approval card</div>
            <div className="dp-field-v">
              A guarded write shows a card with a summary and the details it will use, with ids
              replaced by names, for example a region name instead of an id. Approve runs exactly
              what was proposed. Decline runs nothing. The card can also list optional details you
              have not set, and Add detail asks the Assistant to propose again with one included.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Who can approve</div>
            <div className="dp-field-v">
              Only the person the proposal was made for. Roles are checked again when you approve,
              not only when it was proposed. A double click or a second tab cannot run it twice.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">15-minute expiry</div>
            <div className="dp-field-v">
              A proposal is valid for 15 minutes. After that it cannot be approved, and the
              Assistant can propose it again.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Resume</div>
            <div className="dp-field-v">
              If you reload or leave the page, open cards come back when you return to the
              conversation. After you approve or decline, the conversation continues on its own and
              the Assistant reports what happened.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Stale-write protection</div>
            <div className="dp-field-v">
              Edits to an existing record note its version when proposed (today{' '}
              <span className="api-inline">update_client</span>,{' '}
              <span className="api-inline">update_form</span>,{' '}
              <span className="api-inline">update_memory</span>,{' '}
              <span className="api-inline">update_checkin</span> and{' '}
              <span className="api-inline">set_checkin_status</span>). If someone changes the record
              before you approve, nothing is written. The Assistant is told to read the record again
              and propose a fresh change, so a late approval cannot overwrite newer work.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Forms</div>
            <div className="dp-field-v">
              Form tools answer that Forms is not enabled when the workspace does not have it, and
              edits go through the same checks as the dashboard.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">OKR hypotheses</div>
            <div className="dp-field-v">
              Hypothesis tools answer that OKRs are not switched on when the workspace does not use
              them. A proposal goes through the same checks as the dashboard: a duplicate title or
              an estimate larger than the remaining gap comes back as a warning, and the Assistant
              proposes again with it acknowledged only after you agree.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Work Inbox</div>
            <div className="dp-field-v">
              Inbox tools answer that the Work Inbox is not switched on when the workspace does not
              have it. They only ever reach your own inbox, never a colleague’s, and never return
              message bodies. Personal threads are shown only when you ask for the Personal category
              by name. See the <Link to="/docs/guides/work-inbox">Work Inbox guide</Link>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Daily Check-ins</div>
            <div className="dp-field-v">
              Check-in tools answer that Check-ins are not enabled when the workspace does not have
              them. Like the Check-ins settings screen, they are for workspace admins only, reads
              included, and they go through the same checks. Nothing deletes a check-in. They are
              not available over MCP.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">03</span>
          File attachments
        </h2>
        <p className="dp-plain">
          You can attach files to a message. The Assistant reads them for that conversation. Types
          are checked from the file contents, not only the name.
        </p>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">PDF</div>
            <div className="dp-field-v">Up to 20 MB each.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Images</div>
            <div className="dp-field-v">JPEG, PNG, GIF and WebP, up to about 3.75 MB each.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Text</div>
            <div className="dp-field-v">
              Plain text, Markdown, CSV, tab-separated and JSON, up to 256 KB each and 512 KB across
              the conversation.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Per conversation</div>
            <div className="dp-field-v">At most 20 attachments and 22 MB in total.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Unsent uploads</div>
            <div className="dp-field-v">
              Files that no sent message uses are deleted after 24 hours.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">04</span>
          Read tools ({reads})
        </h2>
        <p className="dp-plain">
          CRM tools respect pipeline access: a deal in a pipeline you cannot see is not shown to the
          Assistant for you. The Assistant also knows which dashboard page you are on, and the
          record on it, so you can ask about what you are looking at. It only hears about records
          you could already open.
        </p>
        <Groups groups={READS} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">05</span>
          Low-risk writes, no approval ({lows})
        </h2>
        <Groups groups={LOW} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">06</span>
          Guarded writes, approval required ({guarded})
        </h2>
        <Groups groups={GUARDED} />
      </section>

      <section className="dp-section">
        <h2>Related</h2>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/assistant" style={{ color: 'inherit', textDecoration: 'none' }}>
              The Revvly Assistant ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/reference/mcp-tools"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Claude MCP tool catalogue ›
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
